//#region Imports
import React, { useState, useEffect, Fragment } from "react";
import sharedDatas from "../../businessLogic/sharedDatas";
import { useNavigate } from "react-router-dom";

//oneFront SDK
import {
  useSnackbar,
  useQuery,
  useMutation,
  useTranslation,
} from "@1f/react-sdk";

//Vapor Components
import Grid from "@vapor/react-material/Grid";
import Stack from "@vapor/react-material/Stack";
import Button from "@vapor/react-material/Button";
import Autocomplete from "@vapor/react-material/Autocomplete";
import TextField from "@vapor/react-material/TextField";

import VaporPage from "@vapor/react-custom/VaporPage";
import VaporToolbar from "@vapor/react-custom/VaporToolbar";

//Icons
import {
  SaveOutlined as SaveIcon,
  CloseOutlined as CancelIcon,
} from "@mui/icons-material";

//Custom components
import { Loading } from "../../components/Loading";
import { NotAuth } from "../../components/NotAuth";

import {  
  pathAziendaHome,
  pathAziendaAdd,
  pathHome,
} from "../../businessLogic/constants/path";


import {
  getLocalStorageSelectedAzienda,
  setLocalStorageSelectedAzienda,
} from "../../businessLogic/helpers/storageHelpers";
import {
  aziendaModel,
  baseComuneModel,
} from "../../businessLogic/models";


import {
  query_base_all,
  query_base_comune,
} from "../../businessLogic/query/baseTablesQueries";

import { mutation_add_audit } from "../../businessLogic/mutations/auditMutations";
import { mutation_add_lastActivity_byUserId, mutation_delete_lastActivity } from "../../businessLogic/mutations/lastActivityMutations";
import { LogApi } from "../../businessLogic/api/logApi";

//Other components
import { v4 as uuidv4 } from "uuid";
import postal from "postal";
import { AziendaApi } from "../../businessLogic/api/aziendaApi";
import { BaseTableApi } from "../../businessLogic/api/baseTableApi";
import { isTenantReadOnly } from "../../businessLogic/helpers/licenseHelpers";
//#endregion

export const AziendaAdd = ({ basePath }) => {
  const navigate = useNavigate();
  if (
    sharedDatas.getPropertyByName("userId") === "" ||
    sharedDatas.getPropertyByName("tenantId") === "" ||
    sharedDatas.getPropertyByName("aziendaId") === 0
  ) {
    navigate(pathHome);
  }

  const tenantId = sharedDatas.getPropertyByName("tenantId");
  const tenant = sharedDatas.getPropertyByName("tenantModel");
  const aziendaId = sharedDatas.getPropertyByName("aziendaId");
  const loggedUserId = sharedDatas.getPropertyByName("userId");
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const selectedAzienda = getLocalStorageSelectedAzienda();

  //Api
  const logApi = LogApi();
  const aziendaApi = AziendaApi();
  const baseTableApi = BaseTableApi();
  
  //#region Query
  const apiBaseAll = useQuery(
    query_base_all,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiBaseComune = useQuery(
    query_base_comune,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  //#endregion

  //#region Mutation
  const apiAddAudit = useMutation(mutation_add_audit, {
    graphqlEndpoint: "aml:hasura:api://v1/graphql",
  });
  const apiAddLastActivity = useMutation(mutation_add_lastActivity_byUserId, {
    graphqlEndpoint: "aml:hasura:api://v1/graphql",
  });
  const apiDeleteLastActivity = useMutation(mutation_delete_lastActivity, {
    graphqlEndpoint: "aml:hasura:api://v1/graphql",
  });
  //#endregion  

  //#region UseStates
  const [backDropOpen, setBackDropOpen] = useState(true); //Loader utilizzato durante il caricamento
  const [isDataLoaded, setIsDataLoaded] = useState(false); //Indica lo stato di caricamento del result Api
  const [isUserOwner] = useState(sharedDatas.getPropertyByName("userModel")?.type === "OWNER");
  const isExistingAzienda = Number(selectedAzienda) > 0;
  const canAccess = isUserOwner;
  const canSave = canAccess && !isTenantReadOnly(tenant);
  //Tabelle di base
  const [basePaeseList, setBasePaeseList] = useState([]);
  const [baseProvinciaList, setBaseProvinciaList] = useState([]);
  const [baseComuneList, setBaseComuneList] = useState([]);
  const [currentAzienda, setCurrentAzienda] = useState(
    new aziendaModel(
      0,
      tenantId,
      "",
      "",
      "",
      0,
      0,
      "",
      0,
      "",
      "",
      "",
      "",
      new Date(),
      new Date(),
      loggedUserId,
      "",
      0
    )
  );
  const [currentAziendaOriginal, setCurrentAziendaOriginal] = useState(
    new aziendaModel(
      0,
      tenantId,
      "",
      "",
      "",
      0,
      0,
      "",
      0,
      "",
      "",
      "",
      "",
      new Date(),
      new Date(),
      loggedUserId,
      "",
      0
    )
  );
  //Valori form
  const [isAddrPaeseIta, setIsAddrPaeseIta] = useState(true);
  //#endregion

  useEffect(() => {
    loadDatas();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataLoaded]);

  //#region Load Methods
  async function loadDatas() {
    try {
      if (!isUserOwner) return;
      if (
        sharedDatas.getPropertyByName("tenantId") === "" ||
        sharedDatas.getPropertyByName("tenantModel") === null ||
        sharedDatas.getPropertyByName("aziendaId") === 0 ||
        sharedDatas.getPropertyByName("aziendaModelList") === null ||
        sharedDatas.getPropertyByName("userId") === "" ||
        sharedDatas.getPropertyByName("userModel") === null
      ) {
        navigate("/");
      } else {
        setBackDropOpen(true);

        if (!isDataLoaded) {          
          /**************************************/
          //Carico le tabelle di base
          const baseAllResponse = await apiBaseAll.fetch({
            tenantId: tenantId,
          });
          const paesi = baseTableApi.GetBasePaese(baseAllResponse);          
          setBasePaeseList(paesi);
          const province = baseTableApi.GetBaseProvincia(baseAllResponse);          
          setBaseProvinciaList(province);

          /**************************************/
          //Carico l'azienda
          const selectedAziendaResponse = await selectedAzienda;
          const itemId = parseInt(selectedAziendaResponse);
          let azienda = new aziendaModel();

          if (itemId > 0) {
            const aziendaResponse = await aziendaApi.GetAziendaById(itemId,tenantId);
            if(aziendaResponse){
              azienda = new aziendaModel(
                aziendaResponse.id,
                aziendaResponse.tenantId,
                aziendaResponse.denominazione,
                aziendaResponse.cf,
                aziendaResponse.pIva,
                aziendaResponse.addrPaeseId,
                aziendaResponse.addrProvinciaId,
                aziendaResponse.addrProvincia,
                aziendaResponse.addrComuneId,
                aziendaResponse.addrComune,
                aziendaResponse.addrIndirizzo,
                aziendaResponse.addrN,
                aziendaResponse.addrCap,
                aziendaResponse.createdOn,
                aziendaResponse.modifiedOn,
                aziendaResponse.createdBy,
                aziendaResponse.modifiedBy,
                aziendaResponse.meteringSlotId
              );
              setCurrentAzienda(azienda);
              setCurrentAziendaOriginal(azienda);
  
              if (azienda.addrPaeseId !== null && azienda.addrPaeseId !== 0) {
                const paese = paesi.filter(
                  (item) => item.id === azienda.addrPaeseId
                )[0];
                if (paese.identificativo === "ITA") {
                  const provincia = province.filter(
                    (item) => item.id === azienda.addrProvinciaId
                  )[0];                  
                  loadBaseComune(provincia.sigla);
                }
  
                setIsAddrPaeseIta(paese.identificativo === "ITA");
              }
  
              //Last Activity
              await apiDeleteLastActivity.fetch({
                tenantId: tenantId,
                aziendaId: aziendaId,
                userId: loggedUserId,
              });
              await apiAddLastActivity.fetch({
                tenantId: tenantId,
                aziendaId: aziendaId,
                path: pathAziendaAdd,
                entityId: azienda.id.toString(),
                entityName: azienda.denominazione,
              });
  
              setIsDataLoaded(true);
            }            
          }
        }
      }
    } catch (e) {
      logApi.AddLog(e.message, e.stack, "loadDatas", "AziendaAdd");
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      setBackDropOpen(false);
      setIsDataLoaded(true);
    }
  }
  const loadBaseComune = async (provincia) => {
    try {
      const comuneResponse = await apiBaseComune.fetch({
        provincia: provincia,
      });
      const arrItems = [];
      comuneResponse.data.data.base_comune.map((item) =>
        arrItems.push(
          new baseComuneModel(item.Id, item.Codice, item.Provincia, item.Nome)
        )
      );
      setBaseComuneList(arrItems);
    } catch (e) {
      logApi.AddLog(e.message, e.stack, "loadBaseComune", "AziendaAdd");
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    }
  };
  //#endregion

  //#region Handlers
  //Home Button
  const handleGoHome = () => {
    setLocalStorageSelectedAzienda(0);
    navigate(basePath);
  };
  //Salvataggio degli inputs
  const handleChange = (event, value) => {
    if (!canSave) return;
    const { id } = event.target;

    if (id === "txtCf") {
      currentAzienda.cf = event.target.value;
    } else if (id === "txtDenominazione") {
      currentAzienda.denominazione = event.target.value;
    } else if (id === "txtPIva") {
      currentAzienda.pIva = event.target.value;
    } else if (
      id.indexOf("ddlAddrPaese-option") !== -1 ||
      id === "ddlAddrPaese"
    ) {
      currentAzienda.addrPaeseId = value.id;

      currentAzienda.addrProvincia = "";
      currentAzienda.addrComune = "";
      currentAzienda.addrProvinciaId = null;
      currentAzienda.addrComuneId = null;

      var country = basePaeseList.filter(
        (item) => item.id === currentAzienda.addrPaeseId
      )[0];

      setIsAddrPaeseIta(country.identificativo === "ITA");
    } else if (
      id.indexOf("ddlAddrProvincia-option") !== -1 ||
      id === "ddlAddrProvincia"
    ) {
      currentAzienda.addrProvinciaId = value.id;

      //Recupero le città
      var provincia = baseProvinciaList.filter(
        (item) => item.id === currentAzienda.addrProvinciaId
      )[0];
      loadBaseComune(provincia.sigla);
    } else if (
      id.indexOf("ddlAddrComune-option") !== -1 ||
      id === "ddlAddrComune"
    ) {
      currentAzienda.addrComuneId = value.id;
    } else if (id === "txtAddrProvincia") {
      currentAzienda.addrProvincia = event.target.value;
    } else if (id === "txtAddrComune") {
      currentAzienda.addrComune = event.target.value;
    } else if (id === "txtAddrIndirizzo") {
      currentAzienda.addrIndirizzo = event.target.value;
    } else if (id === "txtAddrN") {
      currentAzienda.addrN = event.target.value;
    } else if (id === "txtAddrCap") {
      currentAzienda.addrCap = event.target.value;
    }

    setCurrentAzienda(currentAzienda);
  };
  const handleCancelClick = () => {
    setLocalStorageSelectedAzienda(0);
    navigate(basePath);
  };
  const handleSaveClick = async () => {
      if (!isDataLoaded || !isUserOwner ||
        isTenantReadOnly(sharedDatas.getPropertyByName("tenantModel")) ||
        (isExistingAzienda && currentAzienda.id <= 0)) return; 
    try {
      setBackDropOpen(true);

      if (
        currentAzienda.cf !== undefined &&
        currentAzienda.cf !== "" &&
        currentAzienda.pIva !== undefined &&
        currentAzienda.pIva !== "" &&
        currentAzienda.denominazione !== undefined &&
        currentAzienda.denominazione !== "" &&
        currentAzienda.addrPaeseId !== undefined &&
        currentAzienda.addrPaeseId > 0 &&
        ((currentAzienda.addrProvinciaId !== undefined &&
          currentAzienda.addrProvinciaId > 0) ||
          (currentAzienda.addrProvincia !== undefined &&
            currentAzienda.addrProvincia !== "")) &&
        ((currentAzienda.addrComuneId !== undefined &&
          currentAzienda.addrComuneId > 0) ||
          (currentAzienda.addrComune !== undefined &&
            currentAzienda.addrComune !== "")) &&
        currentAzienda.addrIndirizzo !== undefined &&
        currentAzienda.addrIndirizzo !== "" &&
        currentAzienda.addrCap !== undefined &&
        currentAzienda.addrCap !== ""
      ) {        
        if (currentAzienda.id > 0) {
          //Se esiste già il record aggiorno le informazioni
          let updateAziendaResponse = await aziendaApi.UpdateAzienda(currentAzienda);
          if(updateAziendaResponse){
            let aziendaResultModel = new aziendaModel(
              updateAziendaResponse.Id,
              updateAziendaResponse.TenantId,
              updateAziendaResponse.Denominazione,
              updateAziendaResponse.Cf,
              updateAziendaResponse.PIva,
              updateAziendaResponse.AddrPaeseId,
              updateAziendaResponse.AddrProvinciaId,
              updateAziendaResponse.AddrProvincia,
              updateAziendaResponse.AddrComuneId,
              updateAziendaResponse.AddrComune,
              updateAziendaResponse.AddrIndirizzo,
              updateAziendaResponse.AddrN,
              updateAziendaResponse.AddrCap,
              updateAziendaResponse.CreatedOn,
              updateAziendaResponse.ModifiedOn,
              updateAziendaResponse.CreatedBy,
              updateAziendaResponse.ModifiedBy,
              updateAziendaResponse.MeteringSlotId
            );
  
            //Aggiungo la Audit per tracciare l'azione
            apiAddAudit.fetch({
              tenantId: tenantId,
              aziendaId: aziendaId,
              area: "azienda",
              operation: "Update",
              entityType: "azienda",
              entityId: aziendaResultModel.id.toString(),
              oldValues: JSON.stringify(currentAziendaOriginal),
              newValues: JSON.stringify(aziendaResultModel),
            });
  
            enqueueSnackbar(t("message.success"), { variant: "success" });
          }                                
        } else {
          //Se sto creando l'azienda
          if (currentAzienda.id === 0) {
            let addAziendaResponse = await aziendaApi.PostAzienda(currentAzienda,tenantId);
            if(addAziendaResponse){
              let aziendaResultModel = new aziendaModel(
                addAziendaResponse.Id,
                addAziendaResponse.TenantId,
                addAziendaResponse.Denominazione,
                addAziendaResponse.Cf,
                addAziendaResponse.PIva,
                addAziendaResponse.AddrPaeseId,
                addAziendaResponse.AddrProvinciaId,
                addAziendaResponse.AddrProvincia,
                addAziendaResponse.AddrComuneId,
                addAziendaResponse.AddrComune,
                addAziendaResponse.AddrIndirizzo,
                addAziendaResponse.AddrN,
                addAziendaResponse.AddrCap,
                addAziendaResponse.CreatedOn,
                addAziendaResponse.ModifiedOn,
                addAziendaResponse.CreatedBy,
                addAziendaResponse.ModifiedBy,
                addAziendaResponse.MeteringSlotId
              );
  
              //Aggiungo la Audit per tracciare l'azione              
              apiAddAudit.fetch({
                tenantId: tenantId,
                aziendaId: aziendaId,
                area: "azienda",
                operation: "Create",
                entityType: "azienda",
                entityId: aziendaResultModel.id.toString(),
                oldValues: "",
                newValues: JSON.stringify(aziendaResultModel),
              });              
            }                          
          }
        }

        //Recupero e aggiorno il nuovo elenco di soggetti obbligati
        const aziendeResponse = await aziendaApi.GetAllAziende(tenantId);
        const arrAziende = [];
        aziendeResponse.map((item) =>
          arrAziende.push(
            new aziendaModel(
              item.id,
              item.tenantId,
              item.denominazione,
              item.cf,
              item.pIva,
              item.addrPaeseId,
              item.addrProvinciaId,
              item.addrProvincia,
              item.addrComuneId,
              item.addrComune,
              item.addrIndirizzo,
              item.addrN,
              item.addrCap,
              item.createdOn,
              item.modifiedOn,
              item.createdBy,
              item.modifiedBy,
              item.meteringSlotId
            )
          )
        );

        sharedDatas.setPropertyValue("aziendaModelList", arrAziende);

        //Invio una notifica tramite pub/sub interno
        postal.publish({
          channel: "azienda",
          topic: "azienda.update",
          data: {
            aziende: arrAziende,
          },
        });

        navigate(pathAziendaHome);        
      } else {
        enqueueSnackbar(t("error.missingFields"), { variant: "error" });
      }
    } catch (e) {
      logApi.AddLog(e.message, e.stack, "handleSaveClick", "AziendaAdd");
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      setBackDropOpen(false);
    }
  };
  //#endregion  

  //#region Return Graphics
  return (
    <Fragment>
      <VaporPage
        title={
          currentAzienda.id === 0
            ? t("azienda.titoloAdd")
            : t("azienda.titoloManage")
        }
        headerLeft={
          <Button
            variant="text"
            size="medium"
            color="primary"
            onClick={handleGoHome}
            sx={{ ml: 2 }}
          >
            {t("autovalutazione.autovalutazioneAdd.back")}
          </Button>
        }
      >
        <Loading open={backDropOpen} />

        {isDataLoaded && !canAccess ? <NotAuth /> : null}

        {isDataLoaded && canAccess ? (
          <VaporPage.Section>
            <Stack component="fieldset" disabled={!canSave} direction="column" spacing={2} sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
              <Grid container>
                <Grid item xs={3} sx={{ pr: 2 }}>
                  <TextField
                    id="txtCf"
                    key={uuidv4()}
                    label={t("anagrafica.ImportDesktop.codFisc")}
                    defaultValue={currentAzienda.cf}
                    size="small"
                    fullWidth
                    required
                    onChange={handleChange}
                    inputProps={{ autocomplete: "nope",maxLength:16}}
                    readOnly = {currentAzienda.cf}
                  />
                </Grid>
                <Grid item xs={3} sx={{ pr: 2 }}>
                  <TextField
                    id="txtPIva"
                    key={uuidv4()}
                    label={t("fascicolo.fascicoloNew.vatNumber")}
                    defaultValue={currentAzienda.pIva}
                    size="small"
                    fullWidth
                    required
                    onChange={handleChange}
                    inputProps={{ autocomplete: "nope",maxLength:11 }}
                    readOnly = {currentAzienda.pIva}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="txtDenominazione"
                    key={uuidv4()}
                    label={t("anagrafica.ImportDesktop.denom")}
                    defaultValue={currentAzienda.denominazione}
                    size="small"
                    fullWidth
                    required
                    onChange={handleChange}
                    inputProps={{ autocomplete: "nope",maxLength:500 }}
                    readOnly = {tenant.identifier === currentAzienda.cf}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={4} sx={{ pr: 2 }}>
                  <Autocomplete
                    id="ddlAddrPaese"
                    key={uuidv4()}
                    size="small"
                    fullWidth
                    options={basePaeseList}
                    sx={{ width: "100%" }}
                    isOptionEqualToValue={(option, value) =>
                      currentAzienda.addrPaeseId === undefined
                        ? option.id === value.id
                        : option.id === currentAzienda.addrPaeseId
                    }
                    getOptionLabel={(option) => option.nome}
                    defaultValue={
                      currentAzienda.addrPaeseId !== undefined
                        ? basePaeseList.filter(
                            (item) => item.id === currentAzienda.addrPaeseId
                          )[0]
                        : null
                    }
                    renderInput={(params) => (
                      <TextField {...params} required label="Nazione" />
                    )}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={3} sx={{ pr: 2 }}>
                  {isAddrPaeseIta ? (
                    <Autocomplete
                      id="ddlAddrProvincia"
                      key={uuidv4()}
                      size="small"
                      fullWidth
                      options={baseProvinciaList}
                      sx={{ width: "100%" }}
                      isOptionEqualToValue={(option, value) =>
                        currentAzienda.addrProvinciaId === undefined
                          ? option.id === value.id
                          : option.id === currentAzienda.addrProvinciaId
                      }
                      getOptionLabel={(option) => option.nome}
                      defaultValue={
                        currentAzienda.addrProvinciaId !== undefined
                          ? baseProvinciaList.filter(
                              (item) =>
                                item.id === currentAzienda.addrProvinciaId
                            )[0]
                          : null
                      }
                      renderInput={(params) => (
                        <TextField {...params} required label="Provincia" />
                      )}
                      onChange={handleChange}
                    />
                  ) : (
                    <TextField
                      id="txtAddrProvincia"
                      key={uuidv4()}
                      label={t("prestazione.form.provincia")}
                      size="small"
                      defaultValue={currentAzienda.addrProvincia}
                      fullWidth
                      required
                      onChange={handleChange}
                      inputProps={{ autocomplete: "nope",maxLength:255 }}
                    />
                  )}
                </Grid>
                <Grid item xs={5}>
                  {isAddrPaeseIta ? (
                    <Autocomplete
                      id="ddlAddrComune"
                      key={uuidv4()}
                      size="small"
                      fullWidth
                      options={baseComuneList}
                      sx={{ width: "100%" }}
                      isOptionEqualToValue={(option, value) =>
                        currentAzienda.addrComuneId === undefined
                          ? option.id === value.id
                          : option.id === currentAzienda.addrComuneId
                      }
                      getOptionLabel={(option) => option.nome}
                      defaultValue={
                        currentAzienda.addrComuneId !== undefined
                          ? baseComuneList.filter(
                              (item) => item.id === currentAzienda.addrComuneId
                            )[0]
                          : null
                      }
                      renderInput={(params) => (
                        <TextField {...params} required label="Comune" />
                      )}
                      onChange={handleChange}
                    />
                  ) : (
                    <TextField
                      id="txtAddrComune"
                      key={uuidv4()}
                      label={t("prestazione.form.comune")}
                      size="small"
                      defaultValue={currentAzienda.addrComune}
                      fullWidth
                      required
                      onChange={handleChange}
                      inputProps={{ autocomplete: "nope",maxLength:255 }}
                    />
                  )}
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={8} sx={{ pr: 2 }}>
                  <TextField
                    id="txtAddrIndirizzo"
                    key={uuidv4()}
                    label={t("prestazione.form.indirizzo")}
                    size="small"
                    defaultValue={currentAzienda.addrIndirizzo}
                    fullWidth
                    required
                    onChange={handleChange}
                    inputProps={{ autocomplete: "nope",maxLength:255 }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pr: 2 }}>
                  <TextField
                    id="txtAddrN"
                    key={uuidv4()}
                    label="N."
                    size="small"
                    defaultValue={currentAzienda.addrN}
                    fullWidth
                    onChange={handleChange}
                    inputProps={{ autocomplete: "nope",maxLength:10 }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    id="txtAddrCap"
                    key={uuidv4()}
                    label="CAP"
                    size="small"
                    defaultValue={currentAzienda.addrCap}
                    fullWidth
                    required
                    onChange={handleChange}
                    inputProps={{ autocomplete: "nope",maxLength:10 }}
                  />
                </Grid>
              </Grid>
            </Stack>
          </VaporPage.Section>
        ) : null}
      </VaporPage>

      {isDataLoaded && canAccess ? (
        <VaporToolbar
          variant="regular"
          size="medium"
          withoutAppBar={false}
          contentRight={
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="secondary"
                size="medium"
                endIcon={<CancelIcon />}
                onClick={handleCancelClick}
              >
                {t("actions.annulla")}
              </Button>
              <Button
                variant="contained"
                size="medium"
                endIcon={<SaveIcon />}
                onClick={handleSaveClick}
                disabled={!canSave}
              >
                {currentAzienda.id > 0
                  ? t("actions.aggiorna")
                  : t("actions.salva")}
              </Button>
            </Stack>
          }
        ></VaporToolbar>
      ) : null}
    </Fragment>
  );
  //#endregion
};
